import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  UploadCloud,
  FileText,
  Loader2,
  CheckCircle2,
  ArrowUpRight,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { parseCv, submitCandidate } from "@/lib/api";
import { useCandidate } from "@/lib/candidate";
import { CV } from "@/constants/testIds";

const EASE = [0.76, 0, 0.24, 1];
const ACCEPT = ".pdf,.docx,.txt";
const MAX_MB = 4;

const formatDetail = (d) => {
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" · ");
  return String(d);
};

export const CvDropModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { setParsedCV, setCvFilename, setCvText, parsedCV } = useCandidate();
  const [status, setStatus] = useState("idle"); // idle | parsing | done | error
  const [error, setError] = useState("");
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [savingQuick, setSavingQuick] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setStatus("idle");
    setError("");
    setFile(null);
    setDragging(false);
    setSavingQuick(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const handleFile = useCallback(
    async (f) => {
      if (!f) return;
      const name = f.name.toLowerCase();
      if (!(name.endsWith(".pdf") || name.endsWith(".docx") || name.endsWith(".txt"))) {
        setStatus("error");
        setError("Please upload a PDF, DOCX or TXT file.");
        return;
      }
      if (f.size > MAX_MB * 1024 * 1024) {
        setStatus("error");
        setError(`File is larger than ${MAX_MB} MB.`);
        return;
      }
      setFile(f);
      setError("");
      setStatus("parsing");
      try {
        const parsed = await parseCv(f);
        setParsedCV(parsed);
        setCvFilename(f.name);
        setCvText(parsed.cv_text_preview || null);
        setStatus("done");
        toast.success("CV parsed — profile ready to review.");
      } catch (err) {
        setStatus("error");
        setError(formatDetail(err?.response?.data?.detail) || err.message);
      }
    },
    [setParsedCV, setCvFilename, setCvText]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    handleFile(f);
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    handleFile(f);
  };

  const onQuickSave = async () => {
    if (!parsedCV) return;
    if (!parsedCV.name || !parsedCV.email) {
      setError("We couldn't detect your name & email — please review the profile.");
      return;
    }
    setSavingQuick(true);
    try {
      await submitCandidate({
        name: parsedCV.name,
        email: parsedCV.email,
        phone: parsedCV.phone || null,
        headline: parsedCV.headline || null,
        current_role: parsedCV.current_role || null,
        current_company: parsedCV.current_company || null,
        years_of_experience: parsedCV.years_of_experience ?? null,
        linkedin_url: parsedCV.linkedin_url || null,
        github_url: parsedCV.github_url || null,
        skills: parsedCV.skills || [],
        summary: parsedCV.summary || null,
        cv_filename: file?.name || null,
        cv_text: parsedCV.cv_text_preview || null,
      });
      toast.success("CV received — we'll be in touch shortly.");
      handleClose();
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSavingQuick(false);
    }
  };

  const onEditProfile = () => {
    handleClose();
    navigate("/apply");
  };

  const onBrowseMatches = () => {
    handleClose();
    navigate("/jobs");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[80] bg-[rgba(20,17,13,0.55)] backdrop-blur-sm flex items-start md:items-center justify-center px-4 py-16 overflow-y-auto"
          onClick={handleClose}
          data-testid={CV.modal}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="relative bg-[var(--bg)] border border-[var(--line-strong)] w-full max-w-2xl p-8 md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              data-testid={CV.close}
              className="absolute top-4 right-4 text-[var(--ink-70)] hover:text-[var(--ink)] p-1"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <p className="overline mb-3">Drop your CV</p>
            <h2 className="font-display text-3xl md:text-4xl tracking-[-0.02em] leading-[1.05] text-[var(--ink)] mb-2">
              Skip the form. Let{" "}
              <span className="font-serif-accent italic text-[var(--accent)]">
                AI
              </span>{" "}
              read your CV.
            </h2>
            <p className="text-[var(--ink-70)] text-sm mb-8">
              PDF, DOCX or TXT · under {MAX_MB} MB. We&apos;ll extract your role,
              skills and links — you review and confirm.
            </p>

            {status === "idle" && (
              <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                className={`border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${
                  dragging
                    ? "border-[var(--accent)] bg-[var(--bg-2)]"
                    : "border-[var(--line-strong)] hover:bg-[var(--bg-2)]"
                }`}
                data-testid={CV.dropzone}
              >
                <UploadCloud className="w-10 h-10 text-[var(--accent)] mx-auto mb-4" strokeWidth={1.4} />
                <p className="font-display text-xl text-[var(--ink)] mb-2">
                  Drop your CV here
                </p>
                <p className="text-sm text-[var(--ink-70)]">
                  or click to browse
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPT}
                  className="hidden"
                  onChange={onPick}
                  data-testid={CV.fileInput}
                />
              </div>
            )}

            {status === "parsing" && (
              <div
                className="border border-[var(--line-strong)] p-10 text-center"
                data-testid={CV.parsing}
              >
                <Loader2 className="w-10 h-10 text-[var(--accent)] mx-auto mb-4 animate-spin" />
                <p className="font-display text-lg text-[var(--ink)] mb-1">
                  Reading {file?.name}…
                </p>
                <p className="text-sm text-[var(--ink-70)]">
                  Extracting skills, role, LinkedIn & GitHub.
                </p>
              </div>
            )}

            {status === "done" && parsedCV && (
              <div className="space-y-6" data-testid={CV.parsed}>
                <div className="border border-[var(--line-strong)] p-6 bg-[var(--bg-2)]">
                  <div className="flex items-start gap-4">
                    <Sparkles className="w-6 h-6 text-[var(--accent)] shrink-0 mt-1" />
                    <div className="flex-1">
                      <p className="font-display text-2xl text-[var(--ink)] leading-tight">
                        {parsedCV.name || "Unnamed candidate"}
                      </p>
                      {parsedCV.headline && (
                        <p className="text-sm text-[var(--ink-80)] mt-1">
                          {parsedCV.headline}
                        </p>
                      )}
                      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                        <Info label="Email" value={parsedCV.email} />
                        <Info label="Phone" value={parsedCV.phone} />
                        <Info label="Current role" value={parsedCV.current_role} />
                        <Info label="Company" value={parsedCV.current_company} />
                        <Info
                          label="Experience"
                          value={
                            parsedCV.years_of_experience != null
                              ? `${parsedCV.years_of_experience} yrs`
                              : null
                          }
                        />
                        <Info label="LinkedIn" value={parsedCV.linkedin_url} truncate />
                        <Info label="GitHub" value={parsedCV.github_url} truncate />
                      </div>
                      {parsedCV.skills?.length > 0 && (
                        <div className="mt-4">
                          <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--ink-50)] mb-2">
                            Skills detected
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {parsedCV.skills.slice(0, 20).map((s) => (
                              <span
                                key={s}
                                className="text-xs text-[var(--ink)] border border-[var(--line-strong)] px-2 py-0.5 rounded-full"
                              >
                                {s}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="text-red-600 text-sm">{error}</div>
                )}

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
                  <button
                    onClick={onBrowseMatches}
                    className="btn-pill"
                    data-testid="cv-browse-matches"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>See matching jobs</span>
                  </button>
                  <button
                    onClick={onEditProfile}
                    className="btn-ghost"
                    data-testid={CV.editProfile}
                  >
                    <span>Review & edit profile</span>
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={onQuickSave}
                    disabled={savingQuick}
                    className="text-xs uppercase tracking-[0.22em] text-[var(--ink-70)] hover:text-[var(--accent)] link-slide disabled:opacity-60"
                    data-testid={CV.quickSave}
                  >
                    {savingQuick ? "Sending…" : "Or send CV directly"}
                  </button>
                </div>
              </div>
            )}

            {status === "error" && (
              <div
                className="border border-red-400/40 bg-red-500/5 p-6 flex items-start gap-3 text-sm"
                data-testid={CV.parseError}
              >
                <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-700 font-medium mb-2">
                    {error || "We couldn't parse this file."}
                  </p>
                  <button
                    onClick={() => {
                      reset();
                    }}
                    className="link-slide text-[var(--ink)] text-xs uppercase tracking-[0.22em]"
                  >
                    Try another file
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Info = ({ label, value, truncate }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--ink-50)]">
      {label}
    </p>
    <p
      className={`text-[13px] text-[var(--ink)] ${truncate ? "truncate" : ""}`}
      title={value || ""}
    >
      {value || "—"}
    </p>
  </div>
);

export default CvDropModal;
